// Pure helpers of the IoT Data Hub -> virtual device migration (dialog and job details), kept out of the
// components so they can be unit-tested without Vuetify.
export const IOT_HUB_MIGRATION_TYPE = 'iotHubMigration';
export const FIREFLY_BRIDGE_TYPE = 'bridge-firefly';
export const NO_DEVICE_DRIVER = 0;

export const SKIP_REASONS = {
  ALREADY_MIGRATED: 'ALREADY_MIGRATED',
  NOT_FIREFLY: 'NOT_FIREFLY',
  NO_EUI: 'NO_EUI',
};

// virtual devices and the IoT Data Hub share the numeric scale; the labels live with the bulk edit
export const OPERATIONAL_STATUS_LABEL_KEYS = {
  0: 'DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.unset',
  1: 'DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.inTransit',
  2: 'DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.readyForOperation',
  3: 'DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.inOperation',
  4: 'DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.temporaryInactive',
  5: 'DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.decommissioned',
};

export const operationalStatusLabel = (code, translate) => {
  const key = OPERATIONAL_STATUS_LABEL_KEYS[code];
  return key ? translate(key) : translate('DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.unset');
};

// only active Firefly connectors can import the devices
export const fireflyConnectors = (bridges) =>
  (bridges || []).filter((bridge) => bridge.type === FIREFLY_BRIDGE_TYPE && bridge.state === 'active');

// Device drivers were migrated from the IoT Data Hub device types keeping their ids, so a driver with the
// same id is the suggested target. Everything else starts as "No device driver".
export function prefillDeviceTypeMapping(deviceTypes, drivers, current = {}) {
  const driverIds = new Set((drivers || []).map((driver) => driver.id));
  const mapping = {};
  (deviceTypes || []).forEach((deviceType) => {
    if (deviceType.id === null || deviceType.id === undefined) {
      return;
    }
    const key = String(deviceType.id);
    if (current[key] !== undefined && (current[key] === NO_DEVICE_DRIVER || driverIds.has(current[key]))) {
      mapping[key] = current[key];
      return;
    }
    mapping[key] = driverIds.has(deviceType.id) ? deviceType.id : NO_DEVICE_DRIVER;
  });
  return mapping;
}

export const isMatchedById = (deviceTypeId, driverId) =>
  deviceTypeId !== null && deviceTypeId !== undefined && driverId === deviceTypeId;

export const mappedDriverCount = (mapping) =>
  Object.values(mapping || {}).filter((driverId) => driverId && driverId !== NO_DEVICE_DRIVER).length;

// the request body of POST /background-runners/iot-hub-migration
export function buildMigrationPayload({
  application,
  tenantId,
  bridgeId,
  parentId,
  groups,
  mapping,
  importOptions,
  statusMode,
  statusValue,
}) {
  const deviceTypeMapping = {};
  Object.entries(mapping || {}).forEach(([deviceTypeId, driverId]) => {
    deviceTypeMapping[deviceTypeId] = driverId && driverId !== NO_DEVICE_DRIVER ? driverId : null;
  });

  return {
    sourceApplicationId: application.id,
    tenantId,
    bridgeId,
    parentId,
    groups: [...new Set((groups || []).map((group) => `${group}`.trim()).filter(Boolean))],
    deviceTypeMapping,
    import: {
      address: Boolean(importOptions.address),
      tags: Boolean(importOptions.tags),
      description: Boolean(importOptions.description),
    },
    operationalStatus: statusMode === 'set' ? { mode: 'set', value: String(statusValue) } : { mode: 'keep' },
  };
}

// "298 in operation, 14 in transit" from the preview's status distribution
export function describeStatusDistribution(distribution, translate) {
  return Object.entries(distribution || {})
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([code, count]) => `${count} ${operationalStatusLabel(code === 'unset' ? 0 : code, translate)}`)
    .join(', ');
}

// created / skipped / failed of a migration runner from its task counts; the task-creation task is
// counted as done too, so it is taken out of "created"
export function migrationResult(tasks) {
  const counts = tasks || {};
  const skipped = counts.skipped || 0;
  const failed = counts.errors || 0;
  const created = Math.max(0, (counts.done || 0) - skipped - 1);
  return { created, skipped, failed };
}

// the fields the job copied, for the job details ("Address, tags, description")
export function importedFieldsLabel(importOptions, translate) {
  const labels = [];
  if (importOptions && importOptions.address) {
    labels.push(translate('BackgroundJobs.BackgroundJobs.migration.fields.address'));
  }
  if (importOptions && importOptions.tags) {
    labels.push(translate('BackgroundJobs.BackgroundJobs.migration.fields.tags'));
  }
  if (importOptions && importOptions.description) {
    labels.push(translate('BackgroundJobs.BackgroundJobs.migration.fields.description'));
  }
  if (!labels.length) {
    return translate('BackgroundJobs.BackgroundJobs.migration.noImportedFields');
  }
  return labels.map((label, index) => (index === 0 ? label : label.toLowerCase())).join(', ');
}

// the status text of one task row in the job details
// The icon next to a task's status in the job details. The creation task carries no color, so the caller
// can use the application's accent color for it.
export function migrationTaskIcon(task) {
  const result = task.result || {};
  if (task.actions && task.actions.params && task.actions.params.taskCreationType) {
    return { icon: 'fas fa-info-circle', color: null };
  }
  if (result.skipped) {
    return { icon: 'fas fa-minus-circle', color: 'warning' };
  }
  if (result.success) {
    return { icon: 'fas fa-check-circle', color: 'success' };
  }
  return { icon: 'fas fa-times-circle', color: 'error' };
}

export function migrationTaskStatus(task, translate) {
  const result = task.result || {};
  if (task.actions && task.actions.params && task.actions.params.taskCreationType) {
    return translate('BackgroundJobs.BackgroundJobs.taskCreation');
  }
  if (result.skipped) {
    switch (result.skipReason) {
      case SKIP_REASONS.ALREADY_MIGRATED:
        return translate('BackgroundJobs.BackgroundJobs.migration.status.alreadyMigrated', {
          id: (result.data && result.data.existingVirtualDeviceId) || '-',
        });
      case SKIP_REASONS.NOT_FIREFLY:
        return translate('BackgroundJobs.BackgroundJobs.migration.status.notFirefly');
      case SKIP_REASONS.NO_EUI:
        return translate('BackgroundJobs.BackgroundJobs.migration.status.noEui');
      default:
        return translate('BackgroundJobs.BackgroundJobs.migration.status.skipped');
    }
  }
  if (result.success) {
    return result.data && result.data.noDeviceDriver
      ? translate('BackgroundJobs.BackgroundJobs.migration.status.createdNoDriver')
      : translate('BackgroundJobs.BackgroundJobs.migration.status.created');
  }
  return translate('BackgroundJobs.BackgroundJobs.error');
}
